import { useParams, Link } from "react-router-dom";
import { useFetch } from "../lib/useFetch";
import { api } from "../lib/api";
import Meter from "../components/Meter";
import ChatPanel from "../components/ChatPanel";
import type { DeviceDetail as Detail } from "../lib/types";

function uptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

export default function DeviceDetail() {
  const { id = "" } = useParams();
  const { data: d, error, loading, reload } = useFetch<Detail>(
    id ? `/api/devices/${id}` : null,
  );

  async function ackAlerts() {
    await api(`/api/devices/${id}/acknowledge-alerts`, { method: "POST" });
    reload();
  }

  if (loading) return <div className="muted">Loading…</div>;
  if (error) return <div className="error">{error}</div>;
  if (!d) return null;

  return (
    <>
      <div className="topbar">
        <Link to="/devices" className="muted">
          ← Devices
        </Link>
        <h1 style={{ marginLeft: 8 }}>{d.hostname}</h1>
        <span className={`pill dot ${d.isOnline ? "ok" : "danger"}`}>
          {d.isOnline ? "online" : "offline"}
        </span>
        <div className="spacer" />
        <span className="muted">{d.client?.name}</span>
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: "1fr 1fr", alignItems: "start" }}
      >
        <div className="grid">
          <div className="card">
            <h2>Health</h2>
            <table>
              <tbody>
                <tr>
                  <th>User</th>
                  <td>{d.username}</td>
                </tr>
                <tr>
                  <th>IP</th>
                  <td>{d.ipAddress ?? "—"}</td>
                </tr>
                <tr>
                  <th>OS</th>
                  <td>
                    {d.os ?? "—"} {d.osVersion ?? ""}
                  </td>
                </tr>
                <tr>
                  <th>Agent</th>
                  <td>{d.agentVersion ?? "—"}</td>
                </tr>
                <tr>
                  <th>Uptime</th>
                  <td>{uptime(d.uptime)}</td>
                </tr>
                <tr>
                  <th>CPU</th>
                  <td>{d.cpuPercent.toFixed(0)}%</td>
                </tr>
                <tr>
                  <th>RAM</th>
                  <td>
                    <Meter used={d.ramUsedGb} total={d.ramTotalGb} />
                  </td>
                </tr>
                <tr>
                  <th>Disk</th>
                  <td>
                    <Meter used={d.diskUsedGb} total={d.diskTotalGb} />
                  </td>
                </tr>
                <tr>
                  <th>Antivirus</th>
                  <td>
                    <span
                      className={`pill ${d.avStatus === "ok" ? "ok" : "danger"}`}
                    >
                      {d.avStatus}
                    </span>{" "}
                    <span className="muted">
                      defs {d.avDefAgeDays}d old
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Patches pending</th>
                  <td>
                    {d.patchesPending > 0 ? (
                      <span className="pill warn">{d.patchesPending}</span>
                    ) : (
                      "0"
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Last backup</th>
                  <td>{d.lastBackup ?? "—"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="row">
              <h2 style={{ margin: 0 }}>Open alerts ({d.alerts.length})</h2>
              <div className="spacer" />
              {d.alerts.length > 0 && (
                <button onClick={ackAlerts}>Acknowledge all</button>
              )}
            </div>
            <table>
              <tbody>
                {d.alerts.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className={`pill dot ${a.severity}`}>
                        {a.severity}
                      </span>
                    </td>
                    <td>
                      {a.title}
                      {a.detail && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {a.detail}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {d.alerts.length === 0 && (
                  <tr>
                    <td className="muted">No open alerts.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card">
            <h2>Recent tickets</h2>
            <table>
              <tbody>
                {d.tickets.map((t) => (
                  <tr key={t.id}>
                    <td className="muted">#{t.number}</td>
                    <td>{t.subject}</td>
                    <td>
                      <span className="pill">{t.status}</span>
                    </td>
                  </tr>
                ))}
                {d.tickets.length === 0 && (
                  <tr>
                    <td className="muted">No tickets.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ChatPanel deviceId={d.id} clientId={d.clientId} />
      </div>
    </>
  );
}
